import MaterialAppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import MuiToolbar from "@mui/material/Toolbar";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Flex from "src/components/flex";
import { useFeature } from "src/components/providers/feature-flags";
import Gutters from "src/components/gutters";
import AdadotLink from "src/components/link";
import { Button, Stack } from "@mui/material";
import Cheeseburger from "src/components/header/cheeseburger";
import { DARK_THEME } from "src/utils/feaure-flag-constants";
import BetaBadge from "src/components/beta-badge";
import UserMenu from "./user-menu";
import NotificationMenu from "./notification-menu";
import Logo from "./logo";
import { useAuth } from "../providers/auth-context";
import { LoadingContext } from "../providers/loading-provider";
import DemoBadge from "../demo-badge";
import Auth from "../../services/auth";

const Link = styled(AdadotLink).attrs({ variant: "plain" })`
    height: 60px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-right: ${({ theme }) => theme.spacing(4)};
    padding-left: ${({ theme }) => theme.spacing(4)};

    &:hover {
        color: ${({ theme }) => theme.palette.primary.main};
    }

    &.active {
        color: ${({ theme }) => theme.palette.primary.main};
    }
`;

const Toolbar = styled(MuiToolbar)`
    height: 68px;
`;

const LinkButtonContainer = styled(Button)`
    font-size: 12px;
    font-weight: 400;
    height: 100%;
    text-transform: none;
    cursor: pointer;
    color: ${({ theme }) => theme.palette.text.primary};
    &:hover {
        color: ${({ theme }) => theme.palette.primary.main};
        background-color: transparent;
    }

    &.active {
        color: ${({ theme }) => theme.palette.primary.main};
    }
`;

const LinkContainerDesktop = styled(Flex)`
    margin-left: ${({ theme }) => theme.spacing(4)};
    height: 100%;
    ${({ theme }) => theme.breakpoints.down("md")} {
        display: none;
    }
`;

const LinkContainerMobile = styled(Flex)`
    margin-left: ${({ theme }) => theme.spacing(4)};
    height: 100%;
    width: 30%;
    display: none;
    ${({ theme }) => theme.breakpoints.down("md")} {
        display: flex;
    }
`;

const propTypes = {
    handleSetDark: PropTypes.func.isRequired,
};

const AppBar = styled(MaterialAppBar)`
    box-shadow: none;
    background-color: ${({ theme }) => theme.palette.background.default};
`;

const Divider = styled.div`
    min-height: 2px;
    z-index: 2;
    ${({ $loading }) => {
        if ($loading)
            return `
    @keyframes placeHolderShimmer {
  0% {
    background-position: -30vw 0
  }
  100% {
    background-position: 70vw 0
  }
}

  animation-duration: 0.5s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: placeHolderShimmer;
  animation-timing-function: linear;
  background: fff;
  background: linear-gradient(to right, #eeeeee 8%, #60d297 18%, #eeeeee 33%);
  width: 100%;
  position: relative;
  -webkit-backface-visibility: hidden
    `;
        return `background-color: #60d297;`;
    }}
`;

const Offset = styled("div")(({ theme }) => theme.mixins.toolbar);

const Header = ({ handleSetDark, isDisemboweled }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const { isLoading } = useContext(LoadingContext);
    const { user, isDemo } = useAuth();
    const darkThemeSwitcherFeature = useFeature(DARK_THEME);
    const isProd = process.env.ENVIRONMENT_NAME === "prod";

    if (isDisemboweled) {
        return (
            <AppBar position="fixed" color="transparent">
                <Gutters py={0}>
                    <Toolbar disableGutters>
                        <Flex width="100%" flex="1 0 auto" height="100%">
                            <AdadotLink variant="plain" to="/" aria-label="Adadot Homepage">
                                <Logo />
                            </AdadotLink>
                            <LinkContainerDesktop justifyContent="flex-end">
                                {user && !isDemo && (
                                    <Box marginLeft={2}>
                                        <UserMenu isDisemboweled />
                                    </Box>
                                )}
                            </LinkContainerDesktop>
                            <LinkContainerMobile>
                                <NotificationMenu />
                                <Cheeseburger isDisemboweled />
                            </LinkContainerMobile>
                        </Flex>
                    </Toolbar>
                </Gutters>
                <Divider $loading={isLoading} />
            </AppBar>
        );
    }
    return (
        <>
            <AppBar position="fixed" color="transparent">
                <Gutters py={0}>
                    <Toolbar disableGutters>
                        <Flex width="100%" flex="1 0 auto" height="100%">
                            <AdadotLink variant="plain" to="/" aria-label="Adadot Homepage">
                                <Logo />
                            </AdadotLink>
                            <LinkContainerDesktop justifyContent="space-between">
                                <div style={{ display: "flex", height: "100%" }}>
                                    <Box
                                        flexShrink={0}
                                        minWidth={0}
                                        style={{ position: "relative", zIndex: 1 }}
                                    >
                                        <Stack
                                            height="100%"
                                            flexDirection="row"
                                            justifyContent="center"
                                            alignItems="center"
                                        >
                                            <LinkButtonContainer
                                                component={Link}
                                                className={
                                                    location.pathname.startsWith("/overview")
                                                        ? "active"
                                                        : null
                                                }
                                                to="/overview"
                                                data-testid="dashboard-link"
                                            >
                                                {t("header.overview")}
                                            </LinkButtonContainer>
                                            <BetaBadge style={{ marginLeft: "-32px" }} />
                                        </Stack>
                                    </Box>
                                    <Box
                                        flexShrink={0}
                                        minWidth={0}
                                        style={{ position: "relative", zIndex: 1 }}
                                    >
                                        <LinkButtonContainer
                                            component={Link}
                                            className={
                                                location.pathname.startsWith("/causal")
                                                    ? "active"
                                                    : null
                                            }
                                            to="/causal"
                                            data-testid="dashboard-link"
                                        >
                                            {t("header.causal")}
                                        </LinkButtonContainer>
                                    </Box>
                                    <Box
                                        flexShrink={0}
                                        minWidth={0}
                                        style={{ position: "relative", zIndex: 1 }}
                                    >
                                        <LinkButtonContainer
                                            component={Link}
                                            className={
                                                location.pathname.startsWith("/navigation") ||
                                                location.pathname.startsWith("/dashboard") ||
                                                location.pathname.startsWith("/repo-view") ||
                                                location.pathname.startsWith("/teams/")
                                                    ? "active"
                                                    : null
                                            }
                                            to="/navigation"
                                            data-testid="navigation-link"
                                        >
                                            {t("header.dashboards")}
                                        </LinkButtonContainer>
                                    </Box>
                                    <Box
                                        flexShrink={0}
                                        minWidth={0}
                                        style={{ position: "relative", zIndex: 1 }}
                                    >
                                        <LinkButtonContainer
                                            component={Link}
                                            className={
                                                location.pathname.startsWith("/case-analysis")
                                                    ? "active"
                                                    : null
                                            }
                                            to="/case-analysis"
                                            data-testid="analysis-link"
                                        >
                                            {t("header.case-analysis")}
                                        </LinkButtonContainer>
                                    </Box>
                                    {!isProd && (
                                        <Box
                                            flexShrink={0}
                                            minWidth={0}
                                            style={{ position: "relative", zIndex: 1 }}
                                        >
                                            <LinkButtonContainer
                                                component={Link}
                                                className={
                                                    location.pathname.startsWith(
                                                        "/personal-profile"
                                                    )
                                                        ? "active"
                                                        : null
                                                }
                                                to="/personal-profile"
                                                data-testid="dashboard-link"
                                            >
                                                {t("header.profile")}
                                            </LinkButtonContainer>
                                        </Box>
                                    )}
                                    <Box
                                        flexShrink={0}
                                        minWidth={0}
                                        style={{ position: "relative", zIndex: 1 }}
                                    >
                                        <Stack
                                            height="100%"
                                            flexDirection="row"
                                            justifyContent="center"
                                            alignItems="center"
                                        >
                                            <LinkButtonContainer
                                                component={Link}
                                                className={
                                                    location.pathname === "/teams" ? "active" : null
                                                }
                                                to="/teams"
                                                data-testid="teams-link"
                                            >
                                                {t("header.teams")}
                                            </LinkButtonContainer>
                                            <DemoBadge style={{ marginLeft: "-32px" }} />
                                        </Stack>
                                    </Box>
                                    <Box
                                        flexShrink={0}
                                        minWidth={0}
                                        style={{ position: "relative", zIndex: 1 }}
                                    >
                                        <LinkButtonContainer
                                            component={Link}
                                            className={
                                                location.pathname.startsWith("/integrations")
                                                    ? "active"
                                                    : null
                                            }
                                            to="/integrations"
                                            data-testid="integrations-link"
                                        >
                                            {t("header.integrations")}
                                        </LinkButtonContainer>
                                    </Box>
                                    <Box
                                        flexShrink={0}
                                        minWidth={0}
                                        style={{ position: "relative", zIndex: 1 }}
                                    >
                                        <LinkButtonContainer
                                            component={Link}
                                            className={
                                                location.pathname.startsWith("/summary")
                                                    ? "active"
                                                    : null
                                            }
                                            to="/summary"
                                            data-testid="summary-link"
                                        >
                                            {t("header.summary")}
                                        </LinkButtonContainer>
                                    </Box>
                                    {!isProd && (
                                        <Box
                                            flexShrink={0}
                                            minWidth={0}
                                            style={{ position: "relative", zIndex: 1 }}
                                        >
                                            <LinkButtonContainer
                                                component={Link}
                                                className={
                                                    location.pathname.startsWith("/achievements")
                                                        ? "active"
                                                        : null
                                                }
                                                to="/achievements"
                                                data-testid="summary-link"
                                            >
                                                {t("header.achievements")}
                                            </LinkButtonContainer>
                                        </Box>
                                    )}
                                </div>
                                <div>
                                    {darkThemeSwitcherFeature && (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    onChange={(e) =>
                                                        handleSetDark(e.target.checked)
                                                    }
                                                    name="dark theme"
                                                    color="primary"
                                                />
                                            }
                                            labelPlacement="start"
                                            label={<Brightness7Icon />}
                                        />
                                    )}
                                </div>
                            </LinkContainerDesktop>
                            <LinkContainerDesktop justifyContent="flex-end">
                                {!isDemo && <NotificationMenu />}
                                {user && !isDemo && (
                                    <Box marginLeft={2}>
                                        <UserMenu />
                                    </Box>
                                )}
                                {isDemo && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            Auth.redirectToLogin();
                                        }}
                                        style={{ minWidth: "140px" }}
                                    >
                                        Register Now
                                    </Button>
                                )}
                            </LinkContainerDesktop>
                            <LinkContainerMobile>
                                {!isDemo && <NotificationMenu />}
                                <Cheeseburger isDisemboweled={isDisemboweled} />
                            </LinkContainerMobile>
                        </Flex>
                    </Toolbar>
                </Gutters>
                <Divider $loading={isLoading} />
            </AppBar>
            <Offset />
        </>
    );
};

Header.propTypes = propTypes;

export default Header;
